/**
 * Theme Skin
 *
 * Handles smart sticky header, scroll-to-top button,
 * dark/light theme switcher, and mega menu toggle.
 *
 * @package Mono
 */

(function () {
  'use strict';

  /**
   * Smart Sticky Header
   *
   * Hides header on scroll down, reveals it on scroll up.
   */
  function initStickyHeader() {
    const header = document.querySelector('.op-smart-sticky');

    if (!header) {
      return;
    }

    let lastScrollY = window.scrollY;

    window.addEventListener('scroll', function () {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }

      lastScrollY = currentScrollY;
    });
  }

  /**
   * Scroll To Top Button
   *
   * Shows/hides the button and renders a conic-gradient progress ring.
   * Click handler is registered once on init, not on every scroll event.
   */
  function initScrollToTop() {
    const toTopBtn = document.querySelector('.op-scroll-to-top');

    if (!toTopBtn) {
      return;
    }

    const progressColor = 'var(--wp--preset--color--primary)';
    const scrollEl = document.documentElement;
    const SHOW_THRESHOLD = 300;

    // Register click handler once.
    toTopBtn.addEventListener('click', function () {
      scrollEl.scrollTop = 0;
    });

    function updateScrollToTop() {
      const scrollPos = scrollEl.scrollTop;
      const scrollable = scrollEl.scrollHeight - scrollEl.clientHeight;
      const scrollValue = scrollable > 0
        ? Math.round((scrollPos / scrollable) * 100)
        : 0;

      toTopBtn.classList.toggle('show-to-top-btn', scrollPos > SHOW_THRESHOLD);
      toTopBtn.style.background = `conic-gradient(${progressColor} ${scrollValue}%, transparent ${scrollValue}%)`;
    }

    window.addEventListener('scroll', updateScrollToTop);

    // Run once on load to set initial state.
    updateScrollToTop();
  }

  /**
   * Theme Switcher
   *
   * Persists dark/light preference in localStorage and
   * syncs the <html> element's data-theme attribute and class.
   */
  function initThemeSwitcher() {
    const toggle = document.getElementById('theme-toggle');
    const htmlEl = document.documentElement;

    const DARK_KEY = 'fpTheme';
    const DARK_VAL = 'dark';
    const DARK_CLASS = 'mono-dark';

    if (!toggle) {
      return;
    }

    /**
     * Apply a theme to the document root.
     *
     * @param {boolean} isDark Whether to apply the dark theme.
     */
    function applyTheme(isDark) {
      htmlEl.setAttribute('data-theme', isDark ? DARK_VAL : 'light');
      htmlEl.classList.toggle(DARK_CLASS, isDark);
      toggle.checked = isDark;
    }

    // Apply saved preference on page load.
    applyTheme(localStorage.getItem(DARK_KEY) === DARK_VAL);

    toggle.addEventListener('change', function () {
      const isDark = toggle.checked;
      applyTheme(isDark);
      localStorage.setItem(DARK_KEY, isDark ? DARK_VAL : 'light');
    });
  }

  /**
   * Mega Menu
   *
   * Toggles the mega menu open/closed on trigger click.
   * Closes on outside click or Escape key.
   */
  function initMegaMenu() {
    const triggers = document.querySelectorAll('.is-megamenu');
    const megaMenuWrapper = document.querySelector('.is-mega-menu-wrapper');
    const body = document.body;

    if (!triggers.length || !megaMenuWrapper) {
      return;
    }

    /**
     * Open the mega menu for a given trigger element.
     *
     * @param {Element} trigger The clicked trigger element.
     */
    function openMegaMenu(trigger) {
      body.classList.add('megamenu-is-open');
      megaMenuWrapper.classList.add('is-active');
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    }

    /**
     * Close the mega menu and reset all trigger states.
     */
    function closeMegaMenu() {
      body.classList.remove('megamenu-is-open');
      megaMenuWrapper.classList.remove('is-active');

      triggers.forEach(function (trigger) {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // Set initial ARIA state and bind click handlers on each trigger.
    triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();

        if (body.classList.contains('megamenu-is-open') && this.classList.contains('is-active')) {
          closeMegaMenu();
        } else {
          closeMegaMenu();
          openMegaMenu(this);
        }
      });
    });

    // Close when clicking outside the menu or any trigger.
    document.addEventListener('click', function (e) {
      const clickedInsideWrapper = megaMenuWrapper.contains(e.target);
      const clickedOnTrigger = [...triggers].some(function (trigger) {
        return trigger.contains(e.target);
      });

      if (!clickedInsideWrapper && !clickedOnTrigger) {
        closeMegaMenu();
      }
    });

    // Close on Escape key.
    document.addEventListener('keydown', function (e) {
      if ('Escape' === e.key) {
        closeMegaMenu();
      }
    });
  }

  /**
   * Bootstrap all components after the DOM is ready.
   */
  document.addEventListener('DOMContentLoaded', function () {
    initStickyHeader();
    initScrollToTop();
    initThemeSwitcher();
    initMegaMenu();
  });

}());